<template>
  <ScrollArea
    ref="numbersScrollRef"
    @bottom="nextPage" >
    <div class="flex column">
      <div
        v-if="accessDenied"
        class="settings--limit-width" >
        <h1 class="settings__title">
          Organisation numbers
        </h1>
        <Forbidden />
      </div>

      <div
        v-else
        class="settings--limit-width">
        <h1 class="settings__title">
          Organisation numbers
        </h1>

        <Spinner
          v-if="!initialNumbersRequestFinished"
          class="mt-5"
          :size="20"
          :thickness="2" />

        <InfoCard
          v-if="initialNumbersRequestFinished && currentOrganisation.id && !availableNumbers.length"
          :flat-icon="SBIconList.WHATSAPP_LOGO_COLOR_2"
          title="Add WhatsApp Business numbers"
          :description="noNumbersDescription"
          action-text="Add"
          @action="openAddNumberModal" />

        <!-- Actions bar -->
        <div
          v-if="initialNumbersRequestFinished && (availableNumbers.length || searchQuery)"
          class="sticky-top" >
          <!-- Search section -->
          <div class="flex space-between settings--box bottom-20">
            <SearchInput
              ref="searchInput"
              v-model="searchQuery"
              :no-debounce="true"
              placeholder="Search numbers..." />

            <b-button
              class="flex center-all"
              @click="openAddNumberModal">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.PLUS"
                :color="SBColors.blackPrimary"
                :size="8"/>
              <span>New</span>
            </b-button>
          </div>
        </div>

        <Spinner
          v-if="loadingCustomFieldsFaked"
          style="height: 65px;"
          class="mt-5 spinner-border-top"
          :thickness="2" />

        <!-- No data info -->
        <div
          v-else-if="initialNumbersRequestFinished && !filteredNumbers.length && searchQuery"
          class="no-data flex center-all">
          <span class="wrapper flex center-all">
            <span class="emoji">😐</span>
            <p>We couldn't find anything for <strong>{{ searchQuery }}</strong></p>
          </span>
        </div>

        <!-- Data grid -->
        <div
          v-else-if="initialNumbersRequestFinished && filteredNumbers.length"
          ref="tableGrid"
          class="table__grid clickable bottom-40">
          <span class="column-name sticky-top sticky-second">Name</span>
          <span class="column-name sticky-top sticky-second">Number</span>
          <span class="column-name sticky-top sticky-second">Members</span>
          <span class="column-name sticky-top sticky-second">Created at</span>
          <span class="column-name sticky-top sticky-second" />

          <span
            v-if="availableNumbers.length"
            class="full-width table-count">NUMBERS ({{ filteredNumbersIds.length }})</span>

          <template
            v-for="(business, index) in availableNumbers" >
            <!-- Name column cell -->
            <div
              :key="`${index}-status`"
              class="grid-item"
              :class="[`hover-${index}`, 'flex', 'flex-align-center', {hide: !filteredNumbersIds.includes(business.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(business)" >
              <span
                v-if="!numberIntegrationMap[business.id] || !numberIntegrationMap[business.id].length"
                class="branch-icon flex center-all emoji-fix" >{{ business.emoji }}</span>
              <span
                v-else-if="numberIntegrationMap[business.id] && numberIntegrationMap[business.id].length === 1"
                :class="['branch-icon', numberIntegrationMap[business.id][0]]" />
              <span
                v-else
                class="branch-icon count" >{{ numberIntegrationMap[business.id].length }}</span>
              <p
                class="ellipsis"
                :inner-html.prop="(!business.name ? 'No name' : business.name) | boldMatch(searchQuery)" />
            </div>

            <!-- Number column cell -->
            <div
              :key="`${index}-url`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredNumbersIds.includes(business.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(business)" >
              <p
                class="ellipsis"
                :inner-html.prop="business.phone | phoneFormat | boldMatch(searchQuery)" />
            </div>

            <!-- Members column cell -->
            <div
              :key="`${index}-role`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredNumbersIds.includes(business.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(business)" >
              <span
                class="settings__pill" >
                {{ memberPerBusinessCount[business.id] }}
              </span>
            </div>

            <!-- Created at column cell -->
            <div
              :key="`${index}-join`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredNumbersIds.includes(business.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(business)" >
              <p
                v-if="business.created_at"
                class="ellipsis">{{ business.created_at | tableDate }}</p>
              <p
                v-else
                class="ellipsis no-table-data">No date</p>
            </div>

            <!-- Menu column cell -->
            <div
              :key="`${index}-action`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`, {hide: !filteredNumbersIds.includes(business.id)}]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(business)" >
              <b-dropdown
                no-caret
                right
                toggle-class="settings__table-action"
                @click.stop.prevent
                @show="menuOpen[index] = true"
                @hide="controlHide($event, index)">
                <template #button-content>
                  <span
                    class="menu-button flex center-all"
                    :class="{active: menuOpen[index]}" />
                </template>
                <b-dropdown-item @click.stop.prevent="editNumber(business)" >Edit</b-dropdown-item>
                <b-dropdown-item @click.stop.prevent="deleteNumber(business)" >Terminate</b-dropdown-item>
              </b-dropdown>
            </div>
          </template>

          <!-- <Spinner
            v-if="numbersMeta.current_page < numbersMeta.last_page"
            style="height: 38px;"
            class="my-3 full-width"
            :size="20"
            :thickness="2" /> -->
        </div>

        <EditNumberModal
          ref="editNumberModal"
          :number="selectedNumber"
          :members="rawMembers"
          :icon-index="availableNumbers.findIndex(business => business.phone === selectedNumber.phone)"
          @edited="editNumberMembers" />

      </div>
    </div>
  </ScrollArea>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { parsePhoneNumber } from 'libphonenumber-js';
import escapeStringRegexp from 'escape-string-regexp';

import ToasterMixin from '@/mixins/Toaster';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SearchInput from '@/components/business/widgets/SearchInput';
import Spinner from '@/components/common/Spinner';
import ScrollArea from '@/components/common/ScrollArea';
import InfoCard from '@/components/common/InfoCard';
import roles from '@/helpers/lists/roles';
import Forbidden from '@/components/errors/Forbidden';
import EditNumberModal from '@/components/business/settings/modals/EditNumber.modal';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

const noNumbersDescription = 'Create AI Number WhatsApp enabled numbers and allow your team members to manage WhatsApp conversations together, from anywhere.';

export default {
  name: 'NumbersSettings',
  components: {
    Spinner,
    SearchInput,
    ScrollArea,
    SBIcon,
    Forbidden,
    InfoCard,
    EditNumberModal,
  },
  mixins: [
    ToasterMixin,
    UserPermissionsMixin,
  ],
  data() {
    return {
      initialNumbersRequestFinished: false,
      searchQuery: '',
      selectedFilters: [],
      filterMenuIsOpen: false,
      filterOptions: [],
      menuOpen: [],
      copyLinkSuccess: false,
      noHide: false,
      forbidden: false,
      roles,
      rawMembers: [],
      memberPerBusinessCount: {},
      membersMeta: {},
      membersPage: 0,
      selectedNumber: {},
      integrationIcon: {
        FRONTAPP: 'integration-front-color.svg',
        FRONTPACHA: 'integration-front-color.svg',
      },
      SBColors,
      SBIconList,
      noNumbersDescription,
      loadingCustomFieldsFaked: false, // Contact fields are not paginated so we will fake the loader for now
    };
  },
  computed: {
    ...mapGetters({
      numbersMeta: 'merchant/membersMeta',
      currentOrganisation: 'organisation/currentOrganisation',
      currentOrganisationBusinessIds: 'organisation/currentOrganisationBusinessIds',
      numberIntegrationMap: 'organisation/numberIntegrationMap',
      organisationNumbers: 'organisation/organisationNumbers',
      apiError: 'merchant/apiError',
      me: 'organisation/me',
    }),
    accessDenied() {
      return this.forbidden || !this.checkPermission('manage_organisation_settings');
    },
    filteredNumbers() {
      return this.availableNumbers.filter(business => (RegExp(`(${escapeStringRegexp(this.searchQuery)})`, 'i').test(business.name) || RegExp(`(${escapeStringRegexp(this.searchQuery.replaceAll(' ', '').replace(/^00/, '+').replace(/^0/, ''))})`).test(business.phone)));
    },
    filteredNumbersIds() {
      return this.filteredNumbers.map(number => number.id);
    },
    availableNumbers() {
      if (this.isAdmin || this.isOwner || this.incognito) {
        return this.organisationNumbers;
      }
      return this.currentOrganisation.businesses;
    },
  },
  watch: {
    accessDenied() {
      if (!this.accessDenied) {
        // this.fetchNumbers();
        this.processNumbers();
      }
    },
    numbers() {
      this.initialNumbersRequestFinished = true;
      this.loadMoreIfNeeded();
    },
    currentOrganisation() {
      if (!this.accessDenied && this.currentOrganisation.id) {
        // this.fetchNumbers();
        this.processNumbers();
      }
    },
    searchQuery() {
      this.loadingCustomFieldsFaked = true;
      setTimeout(() => {
        this.loadingCustomFieldsFaked = false;
      }, 300);
    },
  },
  mounted() {
    if (!this.accessDenied && this.currentOrganisation.id) {
      setTimeout(() => this.fetch(), 2000);
    }
    if (!this.organisationNumbers.length) {
      this.fetchOrganisationNumbers();
    }
    window.eventBus.$on('403', () => { this.forbidden = true; });
    window.eventBus.$on('contact-deleted', this.removeUser);

    this.finalizeSplashView();
  },
  methods: {
    ...mapActions({
      fetchNumbers: 'merchant/fetchMembersWithInvitations',
      fetchAllMembers: 'merchant/fetchAllMembers',
      deleteNumber: 'merchant/deleteInvitation',
      finalizeSplashView: 'organisation/finalizeSplashView',
      fetchOrganisationNumbers: 'organisation/fetchOrganisationNumbers',
    }),
    ...mapMutations({
      updateMemberInvite: 'merchant/UPDATE_MEMBER_INVITE',
    }),
    escapeStringRegexp,
    fetch() {
      // this.fetchNumbers();
      this.processNumbers();
    },
    // Method will get all the members first repeating the get cycle if there are more pages of members. Once it has all the members it will process them.
    processNumbers() {
      if (!this.membersMeta.last_page || this.membersMeta.last_page > this.membersPage) {
        this.membersPage++;
        this.fetchAllMembers(this.membersPage)
          .then((response) => {
            if (response.status === 200) {
              this.rawMembers = [...this.rawMembers, ...response.data.data];
              this.membersMeta = response.data.meta;
              this.processNumbers();
            }
          })
          .catch(() => {

          });
      } else {
        this.initialNumbersRequestFinished = true;
        // this.rawMembers.forEach((member) => { });

        const memberPerBusinessCount = {};
        this.availableNumbers.forEach((business) => {
          memberPerBusinessCount[business.id] = this.rawMembers.filter(member => !member.ai_assistant && member.businesses.map(business => business.uid).includes(business.id)).length;
        });

        this.memberPerBusinessCount = { ...memberPerBusinessCount };
      }
    },
    preventHide() {
      // this.noHide = true;
    },
    controlHide(event, index) {
      if (this.noHide) {
        this.noHide = false;
        event.preventDefault();
      } else {
        this.menuOpen[index] = false;
      }
    },
    deleteNumber(number) {
      const parsedPhone = parsePhoneNumber(number.phone);

      this.$confirmPluginModal.show({
        buttonVariant: 'BLUE',
        title: 'Terminate number',
        message: `In order to terminate number <b>+${parsedPhone.countryCallingCode} ${parsedPhone.formatNational()}</b> please contact our support.`,
        confirmButtonText: 'Contact support',
        showUndoWarning: false,
        confirmCallback: () => {
          this.$confirmPluginModal.close();
          window.open('https://wa.me/14159172497', '_blank');
        },
      });
    },
    editNumber(number) {
      this.selectedNumber = number;
      this.$refs.editNumberModal.show(number);
    },
    editNumberMembers(data) {
      const membersOnBusiness = data.membersData.user_profile_uid.map(member => member[0]);
      const businessId = data.membersData.business_uid[0];
      const businessName = data.nameData.name;

      this.rawMembers.forEach((member) => {
        const businessIndex = member.businesses.findIndex(business => business.uid === businessId);
        if (!(businessIndex >= 0) && membersOnBusiness.includes(member.id)) {
          member.businesses.push({
            name: businessName,
            uid: businessId,
          });
        } else if ((businessIndex >= 0) && !membersOnBusiness.includes(member.id)) {
          member.businesses.splice(businessIndex, 1);
        }
      });

      this.processNumbers();
    },
    openAddNumberModal() {
      window.open(`https://tally.so/r/wgA5KO?email=${this.me.email}&name=${this.currentOrganisation.name}`, '_blank');
    },
    removeHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        cell.classList.remove('hover-grid');
        cell.classList.remove('last');
      }
    },
    addHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        if (index === 0) {
          cell.classList.add('first');
        }
        cell.classList.add('hover-grid');
        if (index === cells.length - 1) {
          cell.classList.add('last');
        }
      }
    },
    nextPage() {
      if (this.numbersMeta.current_page >= this.numbersMeta.last_page) {
        return;
      }
      this.loadPage(this.numbersMeta.current_page + 1);
    },
    loadPage(page) {
      this.fetchNumbers({
        data: {
          page,
        },
        append: true,
      });
    },
    loadMoreIfNeeded() {
      if (!this.$refs.sentTemplatesScrollRef.verticalScrollActive) {
        this.nextPage();
      }
    },
    selectRow(number) {
      this.editNumber(number);
    },
    reload() {
      // this.fetchNumbers();
    },
    removeUser(data) {
      this.rawMembers = [...this.rawMembers.filter(member => member.id !== data.user_uid)];
      this.processNumbers();
    },
  },
};
</script>

<style lang="scss" scoped>
.spinner-border-top {
  border-top: 1px solid #e6ebed;
  margin-top: 0 !important;
  padding-top: 40px;
}

.actions-bar {
  margin-top: 35px;
  margin-bottom: 15px;
}

.table__grid {
  grid-template-columns: minmax(50px, auto) minmax(50px, auto) minmax(auto, 50%) minmax(0, min-content) min-content !important;

  .full-width {
    grid-column: 1 / 6 !important;
  }
}

.branch-icon {
  margin-right: 11px;
  font-size: 12px !important;

  &.count {
    width: 15px;
    text-align: center;
  }

  &.FRONTAPP, &.FRONTPACHA {
    background-image: url('~@/assets/img/SB-Icons/integration-front-color.svg');
    background-size: 14px 14px;
    background-position: center center;
    background-repeat: no-repeat;
    display: block;
    width: 15px;
    height: 15px;
  }
}

::v-deep .copy-text {
  a.dropdown-item {
    padding: 0 !important;

    span {
      display: block;
      padding: 10px 15px;
    }
  }
}

.hide.grid-item {
  display: none !important;
}

.no-table-data {
  color: $greyPrimary;
}
</style>
